import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { labelStarters, titlesForStarters } from "../../components/AskOverlay/starter-copy"
import { buildStarterQuestions } from "../../components/AskOverlay/starters"
import { useAskFlow } from "../../hooks/useAskFlow"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyMutation, useCompanyQuery } from "../../hooks/useCompanyQuery"
import {
  AskSessionNotFoundError,
  deleteAskSession,
  fetchMeetingsPage,
  fetchSeries,
  getAskSession,
  listAskSessions,
} from "../../lib/api"
import { notifyError } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import { useProduct } from "../../lib/tenant"
import { askScopeToBody, type IAskScopeSelection } from "../../lib/types/ask"
import { AskView } from "./-ask/AskView"

interface IAskRouteSearch {
  /** The open chat session — makes a conversation deep-linkable / bookmarkable. */
  session?: string
  /** A question handed over from the ⌘K palette. Asked once, then dropped. */
  q?: string
}

export const Route = createFileRoute("/_auth/ask")({
  component: AskPage,
  validateSearch: (search: Record<string, unknown>): IAskRouteSearch => {
    const out: IAskRouteSearch = {}
    if (typeof search.session === "string" && search.session.length > 0) out.session = search.session
    if (typeof search.q === "string" && search.q.trim().length > 0) out.q = search.q
    return out
  },
})

function AskPage(): React.JSX.Element {
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const product = useProduct()
  const { i18n } = useLingui()
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()
  const search = Route.useSearch()

  const activeSessionId = search.session ?? null

  const [draft, setDraft] = useState("")
  // Retrieval scope for the next question (component state only, not the URL).
  const [scope, setScope] = useState<IAskScopeSelection>({ kind: "all" })

  // --- Queries -------------------------------------------------------------
  const sessionsQuery = useCompanyQuery({
    key: queryKeys.askSessions,
    queryFn: (client) => listAskSessions(client),
  })

  // Series power the scope chips; a missing/empty list simply hides the row.
  const seriesQuery = useCompanyQuery({
    key: queryKeys.series,
    queryFn: (client) => fetchSeries(client),
    staleTime: 5 * 60_000,
  })

  // Recent meetings name the starter questions. Standing questions show
  // immediately; the titles fill in when this resolves. Never open questions.
  const meetingsQuery = useCompanyQuery({
    key: (orgId) => ["ask-starter-meetings", orgId] as const,
    queryFn: (client) => fetchMeetingsPage(client, { limit: 4 }),
    staleTime: 60_000,
  })

  const sessionQuery = useCompanyQuery({
    key: (org) => queryKeys.askSession(org, activeSessionId ?? ""),
    queryFn: (client) => {
      if (!activeSessionId) throw new Error("session unavailable")
      return getAskSession(client, activeSessionId)
    },
    enabled: !!activeSessionId,
    // A vanished session is handled by resetting the URL (effect below), not by
    // hammering the endpoint.
    retry: false,
  })

  // A stale `?session=` that 404s → drop back to a fresh chat.
  useEffect(() => {
    if (sessionQuery.error instanceof AskSessionNotFoundError) {
      navigate({ search: (prev) => ({ ...prev, session: undefined }), replace: true })
    }
  }, [sessionQuery.error, navigate])

  // --- Ask flow (shared with the ask-anywhere overlay) ---------------------
  // The page threads the active session through the URL: a brand-new chat's
  // created id is deep-linked; a vanished session drops `?session=`.
  const onSessionCreated = useCallback(
    (sid: string): void => {
      navigate({ search: (prev) => ({ ...prev, session: sid }), replace: true })
    },
    [navigate],
  )
  const onSessionGone = useCallback((): void => {
    navigate({ search: (prev) => ({ ...prev, session: undefined }), replace: true })
  }, [navigate])
  const clearDraft = useCallback((): void => setDraft(""), [])
  const restoreDraft = useCallback((question: string): void => {
    setDraft((current) => (current.length === 0 ? question : current))
  }, [])

  const { runAsk, pending, errorKind, setErrorKind, busy, clearPending, buildThread } = useAskFlow({
    activeSessionId,
    onSessionCreated,
    onSessionGone,
    onQuestionAccepted: clearDraft,
    onRestoreQuestion: restoreDraft,
  })

  // --- Delete mutation -----------------------------------------------------
  const deleteMutation = useCompanyMutation({
    mutationFn: (client, sessionId: string) => deleteAskSession(client, sessionId),
    onSuccess: (_data, sessionId) => {
      queryClient.removeQueries({ queryKey: queryKeys.askSession(orgId, sessionId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.askSessions(orgId) })
      if (activeSessionId === sessionId) {
        clearPending()
        navigate({ search: (prev) => ({ ...prev, session: undefined }), replace: true })
      }
    },
    onError: (err) => notifyError(err, i18n._(msg`Couldn't delete chat`)),
  })

  // --- Handlers ------------------------------------------------------------
  const submit = useCallback((): void => {
    runAsk(draft, askScopeToBody(scope))
  }, [runAsk, draft, scope])

  const onFollowUp = useCallback(
    (question: string): void => {
      runAsk(question, askScopeToBody(scope))
    },
    [runAsk, scope],
  )

  const onStarter = useCallback(
    (question: string): void => {
      runAsk(question, askScopeToBody(scope))
    },
    [runAsk, scope],
  )

  // The palette links here with `?q=`. Ask it once, then drop the param so a
  // refresh doesn't send it again. The ref blocks a second fire if this effect
  // re-runs before the URL updates.
  const pendingQuestion = search.q?.trim() ?? ""
  const dispatchedQuestion = useRef<string | null>(null)
  useEffect(() => {
    if (pendingQuestion.length === 0) return
    if (dispatchedQuestion.current === pendingQuestion) return
    dispatchedQuestion.current = pendingQuestion
    navigate({ search: (prev) => ({ ...prev, q: undefined }), replace: true })
    runAsk(pendingQuestion, askScopeToBody(scope))
  }, [pendingQuestion, navigate, runAsk, scope])

  const onSelectSession = useCallback(
    (sessionId: string): void => {
      setErrorKind(null)
      clearPending()
      navigate({ search: (prev) => ({ ...prev, session: sessionId }), replace: true })
    },
    [navigate, setErrorKind, clearPending],
  )

  const onNewChat = useCallback((): void => {
    setErrorKind(null)
    clearPending()
    setDraft("")
    navigate({ search: (prev) => ({ ...prev, session: undefined }), replace: true })
  }, [navigate, setErrorKind, clearPending])

  const onDeleteSession = useCallback(
    (sessionId: string): void => {
      deleteMutation.mutate(sessionId)
    },
    [deleteMutation],
  )

  // --- Assemble the display thread ----------------------------------------
  const persisted = activeSessionId ? (sessionQuery.data?.messages ?? []) : []
  const messages = buildThread(persisted)

  const threadLoading = !!activeSessionId && sessionQuery.isLoading && pending === null

  const scopeSeries = (seriesQuery.data ?? []).map((item) => ({ id: item.id, name: item.name }))

  const starters = useMemo(() => {
    const titles = titlesForStarters(meetingsQuery.data?.items ?? [], i18n)
    return labelStarters(buildStarterQuestions(titles, product), i18n)
  }, [meetingsQuery.data, i18n, product])

  return (
    <AskView
      product={product}
      sessions={sessionsQuery.data ?? []}
      sessionsLoading={sessionsQuery.isLoading}
      activeSessionId={activeSessionId}
      onSelectSession={onSelectSession}
      onNewChat={onNewChat}
      onDeleteSession={onDeleteSession}
      messages={messages}
      threadLoading={threadLoading}
      busy={busy}
      errorKind={errorKind}
      onRetry={submit}
      onFollowUp={onFollowUp}
      starters={starters}
      onStarter={onStarter}
      scope={scope}
      onScopeChange={setScope}
      series={scopeSeries}
      composerValue={draft}
      onComposerChange={setDraft}
      onSubmit={submit}
    />
  )
}
